import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { ArrowLeft, Plus, Trash2, Save, ExternalLink, Copy, CheckCheck, Loader2, Eye, Lock, Layers, Send, Mail, MessageSquare, Sparkles, Play, Film } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["in_progress", "review", "delivered", "archived"];

const initialForm = {
  client_name: "",
  business_name: "",
  slug: "",
  preview_url: "",
  access_code: "",
  status: "in_progress",
  notes: "",
};

// -------- Sandbox Pitch Links: share the public /sandbox link via email or SMS
//          and jump straight into the presenter mode from one card.
const SandboxShareCard = () => {
  const publicUrl    = `${window.location.origin}/sandbox`;
  const presenterUrl = `${window.location.origin}/admin/sandbox`;
  const [copied, setCopied] = useState(null);
  const [channel, setChannel] = useState("email");
  const [to, setTo] = useState("");
  const [toName, setToName] = useState("");
  const [busy, setBusy] = useState(false);

  const copy = async (url, key) => {
    await navigator.clipboard.writeText(url);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Link copied");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!to.trim()) return toast.error(channel === "email" ? "Enter an email address" : "Enter a phone number");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/sandbox/share", {
        channel, to: to.trim(), to_name: toName.trim(),
        public_url: publicUrl,
      });
      if (channel === "email") {
        if (data.success) toast.success(`Sandbox link emailed to ${to}`);
        else toast.error("Email failed — check RESEND_API_KEY on production");
      } else {
        if (data.status === "sent") toast.success(`Sandbox link texted to ${to}`);
        else toast.warning(`SMS status: ${data.status}${data.error ? ` · ${data.error}` : ""}`);
      }
      setTo(""); setToName("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-industrial corner-tick p-6 border border-nex-gold/40" data-testid="sandbox-share-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center border border-nex-gold/60 bg-nex-gold/10">
          <Sparkles size={18} className="text-nex-gold" />
        </div>
        <div>
          <div className="mono-label">// SANDBOX PITCH LINKS</div>
          <h2 className="serif-head text-2xl md:text-3xl mt-1">Live demo · anywhere</h2>
          <p className="text-nex-chrome mt-1 text-sm">Send prospects the public walkthrough or launch straight into presenter mode.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        {/* Public link */}
        <div className="border border-nex-border/60 p-4 bg-white/[0.02]">
          <div className="flex items-center justify-between mb-2">
            <div className="mono-label !text-[10px]">// PUBLIC PROSPECT LINK</div>
            <span className="mono-body text-[9px] uppercase tracking-widest text-nex-gold">48h session</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 mono-body text-[11px] text-nex-silver bg-black/30 px-3 py-2 border border-nex-border/40 truncate">{publicUrl}</code>
            <button onClick={() => copy(publicUrl, "public")} className="tick-btn-secondary !py-2 !px-2" data-testid="copy-public" title="Copy">
              {copied === "public" ? <CheckCheck size={13} className="text-emerald-300" /> : <Copy size={13} />}
            </button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="tick-btn-secondary !py-2 !px-2" data-testid="open-public" title="Open">
              <ExternalLink size={13} />
            </a>
          </div>
        </div>

        {/* Presenter link */}
        <div className="border border-nex-gold/50 p-4 bg-nex-gold/[0.04]">
          <div className="flex items-center justify-between mb-2">
            <div className="mono-label !text-[10px]">// PRESENTER MODE</div>
            <span className="mono-body text-[9px] uppercase tracking-widest text-nex-gold">Owner · Reset ready</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 mono-body text-[11px] text-nex-silver bg-black/30 px-3 py-2 border border-nex-border/40 truncate">{presenterUrl}</code>
            <button onClick={() => copy(presenterUrl, "presenter")} className="tick-btn-secondary !py-2 !px-2" data-testid="copy-presenter" title="Copy">
              {copied === "presenter" ? <CheckCheck size={13} className="text-emerald-300" /> : <Copy size={13} />}
            </button>
            <Link to="/admin/sandbox" className="tick-btn-primary !py-2 !px-3 text-xs" data-testid="open-presenter">
              <Play size={13} /> Open
            </Link>
          </div>
        </div>
      </div>

      {/* Tutorial Manager — quick access to upload the 15-sec walkthrough clips */}
      <Link
        to="/admin/tutorials"
        data-testid="open-tutorial-manager"
        className="flex items-center justify-between gap-3 border border-indigo-400/50 bg-indigo-500/10 hover:bg-indigo-500/20 px-4 py-3 transition-colors mb-5 group"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center border border-indigo-400/60 bg-indigo-500/20">
            <Film size={16} className="text-indigo-300" />
          </div>
          <div>
            <div className="mono-label !text-[10px] !text-indigo-300">// TUTORIAL VIDEO MANAGER</div>
            <div className="text-nex-silver text-sm">Upload 15-sec walkthroughs · one per sandbox capability</div>
            <div className="mono-body text-[10px] text-nex-muted mt-0.5">Clips auto-play in-drawer for prospects on first visit</div>
          </div>
        </div>
        <div className="mono-body text-[10px] uppercase tracking-widest text-indigo-300 flex items-center gap-1 group-hover:text-indigo-100">
          Manage clips <ExternalLink size={12} />
        </div>
      </Link>

      {/* Send via email or SMS */}
      <form onSubmit={send} className="border-t border-nex-border/40 pt-4" data-testid="sandbox-share-form">
        <div className="flex items-center gap-2 mb-3">
          <Send size={13} className="text-nex-gold" />
          <div className="mono-label !text-[10px]">// SHARE THE PUBLIC LINK</div>
        </div>
        <div className="grid md:grid-cols-[130px_1fr_1fr_auto] gap-2">
          <div className="flex border border-nex-border">
            {[{ k: "email", Icon: Mail }, { k: "sms", Icon: MessageSquare }].map(({ k, Icon }) => (
              <button key={k} type="button" onClick={() => setChannel(k)} data-testid={`channel-${k}`}
                className={`flex-1 flex items-center justify-center gap-1 mono-body text-[10px] uppercase tracking-widest py-2 transition-colors ${
                  channel === k ? "bg-nex-gold text-black" : "text-nex-chrome hover:text-nex-gold"
                }`}>
                <Icon size={12} /> {k.toUpperCase()}
              </button>
            ))}
          </div>
          <input value={toName} onChange={e=>setToName(e.target.value)} className="input-industrial !py-2 text-sm" placeholder="Recipient name (optional)" data-testid="share-name" />
          <input value={to} onChange={e=>setTo(e.target.value)} className="input-industrial !py-2 text-sm"
            placeholder={channel === "email" ? "prospect@example.com" : "+1 512 555 1234"}
            data-testid="share-to" />
          <button type="submit" disabled={busy} className="tick-btn-primary text-xs !py-2 !px-4" data-testid="share-send">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
          </button>
        </div>
        <div className="mono-body text-[10px] text-nex-muted mt-2">
          Sends a branded {channel === "email" ? "email via Resend" : "SMS via SMSGate Cloud"} with the public sandbox link.
        </div>
      </form>
    </div>
  );
};

const PreviewManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/previews");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setEditing(null);
    setShowForm(false);
  };

  const startCreate = () => {
    setForm(initialForm);
    setEditing(null);
    setShowForm(true);
  };

  const startEdit = (item) => {
    setForm({
      client_name: item.client_name,
      business_name: item.business_name,
      slug: item.slug,
      preview_url: item.preview_url || "",
      access_code: item.access_code || "",
      status: item.status || "in_progress",
      notes: item.notes || "",
    });
    setEditing(item.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await apiClient.patch(`/admin/previews/${editing}`, form);
        toast.success("Preview updated");
      } else {
        await apiClient.post("/admin/previews", form);
        toast.success("Preview created");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this preview slot? The link will stop working immediately.")) return;
    try {
      await apiClient.delete(`/admin/previews/${id}`);
      toast.success("Preview deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = (item) => {
    const url = `${window.location.origin}/preview/${item.slug}${item.access_code ? `?code=${item.access_code}` : ""}`;
    navigator.clipboard.writeText(url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#0A0E17] sticky top-0 z-40 backdrop-blur-xl">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-4">
            <img src={LIFE_LOGO} alt="" className="h-9 w-9 object-contain" />
            <div className="w-px h-8 bg-nex-border" />
            <NexWordmark size="text-xl" />
          </Link>
          <Link to="/admin" data-testid="back-to-dashboard" className="tick-btn-secondary text-xs !py-2 !px-3">
            <ArrowLeft size={13} /> Dashboard
          </Link>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-10 space-y-6">
        <SandboxShareCard />

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="mono-label mb-3">// Prototype Preview Manager</div>
            <h1 className="serif-head text-4xl md:text-5xl">Private client previews.</h1>
            <p className="text-nex-chrome mt-3 max-w-2xl">Create a private URL for each client. Paste your prototype URL, add an optional access code, and share one link. They see progress — nobody else does.</p>
          </div>
          <button onClick={startCreate} data-testid="new-preview-btn" className="tick-btn-primary">
            <Plus size={16} /> New Preview
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={submit} className="card-industrial corner-tick p-6 space-y-5">
            <div className="mono-label">{editing ? "// Editing preview" : "// New preview"}</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="mono-label block mb-2">Client Name</label>
                <input data-testid="pf-client" required type="text" value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} className="input-industrial" placeholder="John Doe" />
              </div>
              <div>
                <label className="mono-label block mb-2">Business Name</label>
                <input data-testid="pf-business" required type="text" value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className="input-industrial" placeholder="Wired SC" />
              </div>
              <div>
                <label className="mono-label block mb-2">Slug <span className="text-nex-muted">(leave blank to auto-generate)</span></label>
                <input data-testid="pf-slug" type="text" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="input-industrial" placeholder="wired-sc-flow" disabled={!!editing} />
              </div>
              <div>
                <label className="mono-label block mb-2">Access Code <span className="text-nex-muted">(optional)</span></label>
                <input data-testid="pf-access" type="text" value={form.access_code} onChange={(e) => setForm({...form, access_code: e.target.value})} className="input-industrial" placeholder="secret code the client will enter" />
              </div>
              <div className="md:col-span-2">
                <label className="mono-label block mb-2">Preview URL (where the actual prototype lives)</label>
                <input data-testid="pf-url" type="url" value={form.preview_url} onChange={(e) => setForm({...form, preview_url: e.target.value})} className="input-industrial" placeholder="https://your-prototype.emergent.host" />
              </div>
              <div>
                <label className="mono-label block mb-2">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button key={s} type="button" data-testid={`pf-status-${s}`} onClick={() => setForm({...form, status: s})} className={`px-3 py-2 text-[12px] mono-body uppercase tracking-widest border transition-colors ${form.status === s ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-silver hover:border-white/30"}`}>
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="mono-label block mb-2">Notes <span className="text-nex-muted">(client-facing progress notes)</span></label>
                <textarea data-testid="pf-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="input-industrial resize-none" placeholder="What's new this iteration, what's next, blockers you need input on..." />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={resetForm} className="tick-btn-secondary">Cancel</button>
              <button type="submit" disabled={busy} data-testid="pf-submit" className="tick-btn-primary disabled:opacity-50">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> {editing ? "Update" : "Create"}</>}
              </button>
            </div>
          </form>
        )}

        {/* Items */}
        {loading ? (
          <div className="py-20 text-center mono-body text-[12px] text-nex-chrome">
            <Loader2 size={16} className="animate-spin inline mr-2" /> Loading previews...
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center">
            <Layers size={32} className="mx-auto text-nex-muted mb-3" />
            <div className="mono-body text-[12px] text-nex-chrome">No prototype previews yet. Create your first one above.</div>
          </div>
        ) : (
          <div className="grid gap-4">
            {items.map((it) => (
              <div key={it.id} data-testid={`preview-${it.id}`} className="card-industrial p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="serif-head text-xl text-nex-silver">{it.business_name}</h3>
                      <span className="mono-body text-[9px] uppercase tracking-widest px-2 py-1 border border-nex-gold/40 text-nex-gold bg-nex-gold/5">{it.status?.replace("_", " ")}</span>
                      {it.access_code && (
                        <span className="inline-flex items-center gap-1 mono-body text-[9px] uppercase tracking-widest px-2 py-1 border border-white/10 text-nex-chrome">
                          <Lock size={9} /> code-gated
                        </span>
                      )}
                    </div>
                    <div className="mono-body text-[11px] mb-2">{it.client_name}</div>
                    <div className="mono-body text-[10px] text-nex-gold">/preview/{it.slug}</div>
                    {it.preview_url && (
                      <div className="mono-body text-[10px] mt-1 truncate">→ {it.preview_url}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => copyLink(it)} data-testid={`copy-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs">
                      {copiedId === it.id ? <><CheckCheck size={12} /> Copied</> : <><Copy size={12} /> Copy Link</>}
                    </button>
                    <a href={`/preview/${it.slug}${it.access_code ? `?code=${it.access_code}` : ""}`} target="_blank" rel="noreferrer" data-testid={`open-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs">
                      <Eye size={12} /> Preview
                    </a>
                    <button onClick={() => startEdit(it)} data-testid={`edit-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs">
                      Edit
                    </button>
                    <button onClick={() => doDelete(it.id)} data-testid={`delete-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs !border-red-500/40 !text-red-400">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {it.notes && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-muted mb-1">// notes</div>
                    <p className="text-nex-silver text-sm whitespace-pre-wrap">{it.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewManager;
